import React from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "primeicons/primeicons.css";

// Define the PDF progress state type
export interface PdfProgressState {
  show: boolean;
  current: number;
  total: number;
  message: string;
}

// Interface for the props
interface DashboardPdfExportProps {
  onExport: () => void;
}

// Component for PDF export button
export const DashboardPdfExportButton: React.FC<DashboardPdfExportProps> = ({
  onExport,
}) => {
  return (
    <button
      className="btn btn-primary"
      onClick={onExport}
      style={{ marginBottom: "15px" }}
    >
      <i className="pi pi-upload me-1" aria-hidden="true"></i> Export Charts as PDF
    </button>
  );
};

// Individual chart template component

// Individual chart template component
export const ChartPdfTemplate: React.FC<{ title: string; chartId: string }> = ({
  title,
  chartId,
}) => {
  return (
    <div
      style={{
        fontFamily: '"Century Gothic", sans-serif',
        padding: "50px",
        backgroundColor: "#fff",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <h2 style={{ textAlign: "center", marginBottom: "30px" }}>{title}</h2>
      <div
        id={chartId}
        style={{ width: "100%", display: "flex", justifyContent: "center" }}
      ></div>

      <div
        style={{
          marginTop: "30px",
          textAlign: "center",
          fontSize: "12px",
          color: "#666",
          position: "absolute",
          bottom: "20px",
          left: "0",
          width: "100%",
        }}
      >
        Generated on {new Date().toLocaleDateString()} - TTEC Career Card
        Dashboard
      </div>
    </div>
  );
};

// Main function to generate and export dashboard as PDF with optional progress handler
export const exportDashboardToPdf = async (
  setPdfProgress?: React.Dispatch<React.SetStateAction<PdfProgressState>>
): Promise<void> => {
  try {
    // If we have a progress handler, show the loader
    if (setPdfProgress) {
      setPdfProgress({
        show: true,
        current: 0,
        total: 7, // Cover page + 6 charts
        message: "Preparing to generate PDF...",
      });
    }

    // Define the chart configurations
    const chartConfigs = [
      {
        selector: "#chart-tenure-current-role",
        title: "Tenure In Current Role",
        pdfId: "pdf-chart-container",
      },
      {
        selector: "#chart-tenure-ttec",
        title: "Tenure With TTEC",
        pdfId: "pdf-chart-container",
      },
      {
        selector: "#chart-willingness-relocate",
        title: "Willingness To Relocate",
        pdfId: "pdf-chart-container",
      },
      {
        selector: "#chart-erg-participation",
        title: "ERG Participation",
        pdfId: "pdf-chart-container",
      },
      {
        selector: "#chart-areas-strengths",
        title: "Areas Of Strengths",
        pdfId: "pdf-chart-container",
      },
      {
        selector: "#chart-areas-development",
        title: "Areas Of Development",
        pdfId: "pdf-chart-container",
      },
    ];

    // Create a PDF with landscape orientation
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    // Create an offscreen div for rendering
    const offscreenDiv = document.createElement("div");
    offscreenDiv.style.position = "absolute";
    offscreenDiv.style.left = "-9999px";
    offscreenDiv.style.top = "-9999px";
    offscreenDiv.style.width = "900px";
    offscreenDiv.style.height = "600px";
    offscreenDiv.style.backgroundColor = "#fff";

    document.body.appendChild(offscreenDiv);

    try {
      const { createRoot } = await import("react-dom/client");
      const root = createRoot(offscreenDiv);

      // Update progress for cover page
      if (setPdfProgress) {
        setPdfProgress((prev) => ({
          ...prev,
          current: 1,
          message: "Generating cover page...",
        }));
      }

      // Add cover page
      root.render(
        <div
          style={{
            fontFamily: '"Century Gothic", sans-serif',
            padding: "50px",
            backgroundColor: "#fff",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <h1 style={{ fontSize: "32px", marginBottom: "30px" }}>
            Dashboard Analytics
          </h1>
          <p style={{ fontSize: "18px" }}>
            Generated on {new Date().toLocaleDateString()}
          </p>
          <img
            src="/ttec-logo-white_Full.png"
            alt="TTEC Logo"
            style={{
              maxWidth: "300px",
              margin: "50px auto",
              opacity: 0.8,
            }}
          />
          <p style={{ fontSize: "16px", position: "absolute", bottom: "50px" }}>
            TTEC Career Card Dashboard
          </p>
        </div>
      );

      await new Promise<void>((resolve) => setTimeout(resolve, 300));

      let canvas = await html2canvas(offscreenDiv, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#fff",
        logging: false,
        allowTaint: true,
      });

      // Add cover page
      const coverImgData = canvas.toDataURL("image/jpeg", 1);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(coverImgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

      // Process each chart on a separate page
      for (let i = 0; i < chartConfigs.length; i++) {
        const chart = chartConfigs[i];
        const originalChart = document.querySelector(chart.selector);

        if (!originalChart) {
          continue;
        }

        // Update progress for current chart
        if (setPdfProgress) {
          setPdfProgress((prev) => ({
            ...prev,
            current: i + 2, // +1 for 1-indexing, +1 for cover page
            message: `Processing chart: ${chart.title}...`,
          }));
        }

        // Add a new page for each chart (except for the first one that follows the cover page)
        if (i > 0) {
          pdf.addPage();
        } else {
          pdf.addPage(); // Add a page after the cover page for the first chart
        }

        // Render the chart template with the title
        root.render(
          React.createElement(ChartPdfTemplate, {
            title: chart.title,
            chartId: chart.pdfId,
          })
        );

        // Wait for render
        await new Promise<void>((resolve) => setTimeout(resolve, 300));

        // Get the container to put the cloned chart into
        const chartContainer = offscreenDiv.querySelector(`#${chart.pdfId}`);
        if (chartContainer) {
          // Clone the chart and add to the template
          const chartClone = originalChart.cloneNode(true) as HTMLElement;
          chartContainer.innerHTML = "";
          chartContainer.appendChild(chartClone);

          // For charts that need extra space
          if (
            chart.selector === "#chart-erg-participation" ||
            chart.selector === "#chart-areas-strengths" ||
            chart.selector === "#chart-areas-development"
          ) {
            (chartContainer as HTMLElement).style.height = "450px";
            (chartContainer as HTMLElement).style.overflow = "visible";
          }

          // Wait for the chart to render in the container
          await new Promise<void>((resolve) => setTimeout(resolve, 500));

          // Capture the rendered chart
          canvas = await html2canvas(offscreenDiv, {
            scale: 2,
            useCORS: true,
            backgroundColor: "#fff",
            logging: false,
            allowTaint: true,
          });

          // Add the chart to the PDF
          const imgData = canvas.toDataURL("image/jpeg", 1);
          pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
        }
      }

      // Update progress to finalization
      if (setPdfProgress) {
        setPdfProgress((prev) => ({
          ...prev,
          message: "Finalizing PDF document...",
        }));
      }

      // Generate filename and save the PDF
      const fileName = `Dashboard_Analytics_${new Date()
        .toISOString()
        .split("T")[0]
        .replace(/-/g, "")}.pdf`;
      pdf.save(fileName);

      // Cleanup
      root.unmount();
      document.body.removeChild(offscreenDiv);

      // Hide the loader
      if (setPdfProgress) {
        setTimeout(() => {
          setPdfProgress((prev) => ({
            ...prev,
            show: false,
            current: 0,
            total: 0,
          }));
        }, 500); // Short delay to ensure PDF download has started
      }
    } catch (error) {

      // Cleanup on error
      if (offscreenDiv.parentNode) {
        document.body.removeChild(offscreenDiv);
      }

      // Hide the loader and show error message
      if (setPdfProgress) {
        setPdfProgress({
          show: false,
          current: 0,
          total: 0,
          message: "",
        });
      }
      alert("Error rendering charts to PDF. Please try again.");
    }
  } catch (error) {

    // Hide the loader if there was an error
    if (setPdfProgress) {
      setPdfProgress({
        show: false,
        current: 0,
        total: 0,
        message: "",
      });
    }
    alert("Error generating PDF. Please try again.");
  }
};
